import { defineStore } from 'pinia';
import { mapProperty } from '~/units/properties/store';
import { PropertiesService } from '~/units/properties/services';
import type {
  PropertiesFilter,
  PropertiesFilterKeys,
  PropertiesFilterRouteQueryKeys,
  PropertiesInitialConfig,
  PropertiesQuery,
  PropertyItem,
  PropertyItemComponent,
} from '~/units/properties/types';
import { PFilterInclusionBase, PFilterSearchType, PFilterSortOptions } from '~/units/properties/types';
import { FetchModes, RepoStates, AppSessionKeys } from '~/units/core/types';
import { useContactsStore } from '~/units/contacts/store';
import { useUsersStore } from '~/units/users/store';
import type { Component } from '~/types';
import { useDataFromState } from '~/composables';
import { useCoreStore } from '~/units/core/store';
import { ROUTES } from '~/constants/ui.constants';
import { RequestName, RequestProxy, useCaller } from '~/hydration/bridges';
import { useWebsiteStore } from '~/units/website/store';

export const usePropertiesStore = defineStore('properties', {
  state: (): {
    propertiesGrid: {
      data: [];
      meta: {
        after_key: string | null;
        total: number;
      } | null;
      isInitialFetched: boolean;
      routeQueryFilters: PropertiesFilterRouteQueryKeys | NonNullable<unknown>;
      initialLoadingConfig: PropertiesInitialConfig;

      awaitingRouteQueryToPush: PropertiesFilterRouteQueryKeys | NonNullable<unknown>;
    };
    specialCriteria: any;
  } => {
    return {
      propertiesGrid: {
        data: [],
        meta: null,
        isInitialFetched: false,
        routeQueryFilters: {},
        initialLoadingConfig: {
          is_available: true,
        },

        awaitingRouteQueryToPush: {},
      },
      specialCriteria: {},
    };
  },
  actions: {
    setSpecialCriteria(key: string, data: any[]) {
      this.specialCriteria[key] = data;
    },

    generateRouteQuery(mode: FetchModes, query: PropertiesQuery, body: PropertiesFilter, order: PFilterSortOptions) {
      const newQuery: { [key: string]: string } = {};

      const inclusionFilterKeys = [
        'new_homes',
        'shared_ownership',
        'retirement_homes',
        'short_let',
        'house_flat_share',
        'pets_allowed',
        'bills_included',
        'furnishing',
      ];

      Object.keys(body).forEach((field: string) => {
        const key = field as PropertiesFilterKeys;
        const value = body[key];

        if (Array.isArray(value)) {
          if (value.length > 0) {
            newQuery[key] = JSON.stringify(value);
          }
        } else if (inclusionFilterKeys.includes(field)) {
          if (value && PFilterInclusionBase.INCLUDE.toString() !== value.toString()) {
            newQuery[key] = value.toString();
          }
        } else if (value) {
          newQuery[key] = value.toString();
        }
      });

      // Handle bait_status_id specifically for URL generation
      let queryForUrl = { ...query };
      if (queryForUrl.bait_status_id && queryForUrl.bait_status_id.length > 0) {
        newQuery.is_completed = 'true';
        // Prevent bait_status_id from being added to the URL directly
        // We delete it from the copy, so the original query object for API call is unaffected
        delete queryForUrl.bait_status_id; 
      } else {
        // Ensure is_completed is not in the URL if bait_status_id is not set
        delete newQuery.is_completed; 
      }

      Object.keys(queryForUrl).forEach((field: string) => {
        const key = field as PropertiesFilterRouteQueryKeys;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        const value = queryForUrl[key];

        // Ensure we don't stringify null/undefined values or empty arrays for the URL
        if (value !== null && value !== undefined) {
          if (Array.isArray(value)) {
            if (value.length > 0) { // Only add if array has items
              newQuery[key] = JSON.stringify(value);
            }
          } else {
            newQuery[key] = value.toString();
          }
        }
      });

      if (order) {
        newQuery.sort = order;
      }

      if (newQuery && Object.keys(newQuery).length) {
        return newQuery;
      }
      // Return empty object if no query params to prevent ? in URL
      return {}; 
    },

    async fetchSingleProperty(component: Component) {
      await callOnce(async () => {
        const nuxtApp = useNuxtApp();

        let slug: string | null = null;
        
        await nuxtApp.runWithContext(async () => {
          const route = useRoute();
          slug = route.params.property_id as string;
        });

        // Fetch users and property in parallel to reduce blocking time
        if (slug) {
          const usersStore = useUsersStore();
          
          // Start both fetches in parallel
          const [usersPromise, propertyPromise] = await Promise.all([
            nuxtApp.runWithContext(async () => {
              return usersStore.fetchUsers();
            }),
            nuxtApp.runWithContext(async () => {
              return fetchPropertyManager(component, slug!);
            }),
          ]);

          // Wait for both to complete before mapping
          await Promise.all([usersPromise, propertyPromise]);
        }

        nuxtApp.runWithContext(() => {
          const route = useRoute();

          const { data } = useDataFromState(RepoStates.SINGLE_PROPERTY_REPO);

          if (data && data[0] && data[0].data) {
            const property = mapProperty({ info: data[0].data }, 0);

            if (route.params.property_slug === 'property') {
              navigateTo({
                path: property.detail_page_url,
                query: route.query,
              });
            }
            const store = useCoreStore();
            store.updateMetaTagsGroup({
              title: property.advert_heading,
              seo_meta_title: property.advert_heading,
              og_meta_title: property.advert_heading,
              seo_meta_description: property.summary || '',
              og_meta_description: property.summary || '',
              og_meta_image: property.image,
            });
          } else {
            navigateTo({
              path: '/not-found',
              query: {
                path: route.path,
              },
            });
          }
        });
      });
    },

    async fetchSinglePropertyById(component: Component, propertyId: string) {
      await callOnce(`fetchSinglePropertyById-${propertyId}`, async () => {
        const nuxtApp = useNuxtApp();

        let slug: string | null = null;
        
        await nuxtApp.runWithContext(async () => {
          slug = propertyId;
        });

        // Fetch users and property in parallel to reduce blocking time
        if (slug) {
          const usersStore = useUsersStore();
          
          // Start both fetches in parallel
          const [usersPromise, propertyPromise] = await Promise.all([
            nuxtApp.runWithContext(async () => {
              return usersStore.fetchUsers();
            }),
            nuxtApp.runWithContext(async () => {
              return fetchPropertyManager(component, slug!);
            }),
          ]);

          // Wait for both to complete before mapping
          await Promise.all([usersPromise, propertyPromise]);
        }

        nuxtApp.runWithContext(() => {
          const route = useRoute();

          const { data } = useDataFromState(RepoStates.SINGLE_PROPERTY_REPO);
          
          let specificPropertyData;
          if (data && Array.isArray(data)) {
            const repoItem = data.find(item => item.identifier === propertyId);
            if (repoItem && repoItem.data) {
              specificPropertyData = repoItem.data;
            }
          }

          if (specificPropertyData) {
            const property = mapProperty({ info: specificPropertyData }, 0);
          } else {
          }
        });
      });
    },

    async fetch(
      mode: FetchModes,
      limit = 9,
      order: PFilterSortOptions,
      searchType: PFilterSearchType,
      query: PropertiesQuery,
      body: PropertiesFilter,
    ) {
      const isInitialRequest = mode === FetchModes.INIT;
      const isInitialRequestSentBefore = !!this.propertiesGrid.isInitialFetched;

      if ((isInitialRequest && !isInitialRequestSentBefore) || !isInitialRequest) {
        const propertiesService = new PropertiesService();
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error

        const requestQuery: PropertiesQuery & {
          search_type: PFilterSearchType;
          is_website: boolean;
          is_available: boolean;
          __limit: number;
          __order_by: PFilterSortOptions;
          after_key?: string;
        } = {
          search_type: searchType,
          is_website: true,
          __limit: limit,
          __order_by: order,
          ...query,
          ...this.specialCriteria,
        };
        if (mode === FetchModes.LOAD_MORE && this.propertiesGrid?.meta?.after_key) {
          requestQuery.after_key = `[${this.propertiesGrid.meta?.after_key}]`;
        }

        const highlightedCriteria = (() => {
          const contactsStore = useContactsStore();
          const sessionData = contactsStore.sessionData;

          if (
            sessionData &&
            sessionData[AppSessionKeys.HIGHLIGHTED_PROPERTIES_CRITERIA] &&
            sessionData[AppSessionKeys.HIGHLIGHTED_PROPERTIES_CRITERIA].criteria
          ) {
            return sessionData[AppSessionKeys.HIGHLIGHTED_PROPERTIES_CRITERIA].criteria;
          }
          return {};
        })();

        const { data, meta } = await propertiesService.fetchProperties(
          {
            ...requestQuery,
            ...highlightedCriteria,
          },
          body,
        );

        if (mode === FetchModes.INIT || mode === FetchModes.LOAD_BY_DATA) {
          this.propertiesGrid.routeQueryFilters = {
            ...body,
            ...query,
            sort: order,
            search_type: searchType,
          };
          
          // Persist to sessionStorage for cross-page navigation
          if (import.meta.client) {
            try {
              sessionStorage.setItem('neuron_properties_filters', JSON.stringify(this.propertiesGrid.routeQueryFilters));
            } catch (e) {
              console.warn('Failed to save filters to sessionStorage:', e);
            }
          }

        }

        if (mode === FetchModes.LOAD_MORE) {
          this.propertiesGrid.data = this.propertiesGrid.data ? [...this.propertiesGrid.data, ...data] : data;
        } else {
          this.propertiesGrid.data = data;
        }
        this.propertiesGrid.meta = meta;
        this.propertiesGrid.isInitialFetched = true;

        if ([FetchModes.SEARCH, FetchModes.SORT].includes(mode)) {
          // Update saved filters for SEARCH and SORT too
          this.propertiesGrid.routeQueryFilters = {
            ...body,
            ...query,
            sort: order,
            search_type: searchType,
          };
          
          // Persist to sessionStorage for cross-page navigation
          if (import.meta.client) {
            try {
              sessionStorage.setItem('neuron_properties_filters', JSON.stringify(this.propertiesGrid.routeQueryFilters));
            } catch (e) {
              console.warn('Failed to save filters to sessionStorage:', e);
            }
          }
          
          const newRouteQuery = this.generateRouteQuery(mode, query, body, order);

          this.handleAwaitingRouteQuery(newRouteQuery);
        } else if (FetchModes.LOAD_BY_DATA === mode) {
          const newRouteQuery = this.generateRouteQuery(mode, query, body, order);

          this.handleAwaitingRouteQuery(newRouteQuery);
        }
      }
    },

    setInitialLoadConfig(config: PropertiesInitialConfig) {
      this.propertiesGrid.initialLoadingConfig = config;
    },

    async handleAwaitingRouteQuery(query: any) {
      if (Object.keys(query).length) {
        if (import.meta.client) {
          const router = useRouter();

          await router.replace({
            query,
          });
          this.propertiesGrid.awaitingRouteQueryToPush = {};
        } else {
          this.propertiesGrid.awaitingRouteQueryToPush = query;
        }
      }
    },

    prepareDefaultSort(component: Component) {
      const value = getVariable(component, 'default_sort-text') as PFilterSortOptions;
      if (value && PFilterSortOptions[value]) {
        return PFilterSortOptions[value];
      }
      return PFilterSortOptions.SUGGESTED;
    },

    async goBackToPropertyList() {
      let savedFilters = this.propertiesGrid.routeQueryFilters as any;
      
      // Try to restore from sessionStorage if store is empty
      if (import.meta.client && (!savedFilters || Object.keys(savedFilters).length === 0)) {
        try {
          const stored = sessionStorage.getItem('neuron_properties_filters');
          if (stored) {
            savedFilters = JSON.parse(stored);
          }
        } catch (e) {
          console.warn('Failed to restore filters from sessionStorage:', e);
        }
      }
      
      const searchType = savedFilters?.search_type;
      
      
      // Determine the correct path based on search_type
      let path = ROUTES.PROPERTIES;
      if (searchType === PFilterSearchType.SALES) {
        path = '/sales/property-for-sale';
      } else if (searchType === PFilterSearchType.LETTINGS) {
        path = '/lettings/property-to-rent';
      }
      
      
      // Generate query parameters from saved filters using the same logic as generateRouteQuery
      const query: { [key: string]: string } = {};
      
      if (savedFilters && Object.keys(savedFilters).length > 0) {
        const inclusionFilterKeys = [
          'new_homes',
          'shared_ownership',
          'retirement_homes',
          'short_let',
          'house_flat_share',
          'pets_allowed',
          'bills_included',
          'furnishing',
        ];

        Object.keys(savedFilters).forEach((key: string) => {
          if (key === 'search_type') {
            // Skip search_type from query params
            return;
          }

          const value = savedFilters[key];

          if (Array.isArray(value)) {
            if (value.length > 0) {
              query[key] = JSON.stringify(value);
            }
          } else if (inclusionFilterKeys.includes(key)) {
            if (value && PFilterInclusionBase.INCLUDE.toString() !== value.toString()) {
              query[key] = value.toString();
            }
          } else if (value !== null && value !== undefined) {
            query[key] = value.toString();
          }
        });
      }

      navigateTo(
        {
          path,
          query: Object.keys(query).length > 0 ? query : undefined,
        },
        {
          external: true,
        },
      );
    },

    async addPotentialViewing(propertyProfileId: number) {
      try {
        const contactsStore = useContactsStore();
        const websitesStore = useWebsiteStore();

        const contactInfo = contactsStore.contactInfo;

        if (contactInfo) {
          const body = {
            contact_id: contactInfo?.contact_id,
            contact_applicant_id: contactInfo.applicant_information?.latest_contact_applicant_id,
            property_profile_id: propertyProfileId,
            company_id: websitesStore.company?.ls_company_id,
          };

          await useCaller(
            RequestProxy.LS_AXON,
            RequestName.ADD_POTENTIAL_VIEWING,
            'presentation/potential_viewings',
            'POST',
            {},
            body,
          );
        }
      } catch (e) {
        console.error(e);
      }
    },
  },
  getters: {
    propertiesGridList: (state): PropertyItemComponent[] => {
      if (state.propertiesGrid.data) {
        return state.propertiesGrid.data
          .filter((item: PropertyItem) => !item.is_featured)
          .map((item, index) => mapProperty(item, index));
      }
      return [];
    },

    highlightedPropertiesGridList: (state): PropertyItemComponent[] => {
      if (state.propertiesGrid.data) {
        return state.propertiesGrid.data
          .filter((item: PropertyItem) => item.is_featured)
          .map((item: PropertyItem, index) => mapProperty(item, index));
      }
      return [];
    },

    propertiesGridCount: (state): number => {
      return state.propertiesGrid.meta?.total || 0;
    },
    isPropertiesGridLoadMoreActive: (state): boolean => {
      const store = usePropertiesStore();

      if (store.highlightedPropertiesGridList.length) {
        return store.propertiesGridCount > store.propertiesGridList.length + store.highlightedPropertiesGridList.length;
      }
      return store.propertiesGridCount > store.propertiesGridList.length;
    },

    propertiesGridInitialRouteQuery: (state) => {
      return state.propertiesGrid.routeQueryFilters;
    },

    propertiesGridInitialLoadConfig: (state) => {
      return state.propertiesGrid.initialLoadingConfig;
    },

    propertiesGridAwaitingRouteQuery(state) {
      return state.propertiesGrid.awaitingRouteQueryToPush;
    },
  },
});
